import React, { useState, useEffect } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { Box, IconButton } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { TargetDetailView } from './WorkspacePanel';
import { useTasksTab } from '../../hooks/Tasks/useTasksTab';
import { PRIMARY_COLOR, STATUS_CONFIG } from './constants';
import { Task, Target, SimpleTargetValue, PersonTargetValue } from './types';
import { tokens } from "../../theme/tokens";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getTargetDisplayText(target: Target): string {
  if (target.type === 'FIELD_RESOURCE') return target.label;
  if (target.type === 'PERSON') return (target.value as PersonTargetValue).name ?? (target.value as SimpleTargetValue).text ?? target.label ?? '';
  return (target.value as SimpleTargetValue).text ?? target.label ?? '';
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface WorkspaceSplitViewProps {
  task: Task;
  selectedTargetId: string;
  onSelectTarget: (id: string) => void;
  onClose: () => void;
}

// ─── CompactTargetRow ─────────────────────────────────────────────────────────

const CompactTargetRow: React.FC<{
  target: Target;
  task: Task;
  isSelected: boolean;
  onSelect: () => void;
}> = ({ target, task, isSelected, onSelect }) => {
  const { markAsWinning } = useTasksTab();
  const isWinner = task.primary_target_ids.includes(target.target_id);
  const dotColor = STATUS_CONFIG[target.status]?.color ?? tokens.color.border.strong;
  const displayText = getTargetDisplayText(target);

  return (
    <Box
      onClick={onSelect}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        px: '10px',
        py: '8px',
        cursor: 'pointer',
        backgroundColor: isSelected ? tokens.color.surface.hoverStrong : 'transparent',
        borderLeft: `2px solid ${isSelected ? PRIMARY_COLOR : 'transparent'}`,
        borderBottom: `1px solid ${tokens.color.border.default}`,
        transition: 'background-color 0.1s ease',
        '&:hover': { backgroundColor: isSelected ? tokens.color.surface.hoverStrong : tokens.color.surface.hover },
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: dotColor, flexShrink: 0 }} />
      <span style={{
        fontSize: '12px',
        color: isSelected ? tokens.color.text.primary : tokens.color.text.primary,
        fontWeight: isSelected ? 500 : 400,
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {displayText || '—'}
      </span>
      <IconButton
        size="small"
        onClick={(e) => { e.stopPropagation(); markAsWinning(task.task_id, target.target_id); }}
        sx={{ p: '2px', color: isWinner ? tokens.color.workflow.doing.fg : `${tokens.color.border.default}`, flexShrink: 0, '&:hover': { color: tokens.color.workflow.doing.fg } }}
      >
        {isWinner ? <StarIcon sx={{ fontSize: '12px' }} /> : <StarBorderIcon sx={{ fontSize: '12px' }} />}
      </IconButton>
    </Box>
  );
};

// ─── WorkspaceSplitView ───────────────────────────────────────────────────────

const WorkspaceSplitView: React.FC<WorkspaceSplitViewProps> = ({ task, selectedTargetId, onSelectTarget, onClose }) => {
  const [detailVisible, setDetailVisible] = useState(false);
  const selectedTarget = task.targets.find((t) => t.target_id === selectedTargetId) ?? null;

  useEffect(() => {
    const id = setTimeout(() => setDetailVisible(true), 10);
    return () => clearTimeout(id);
  }, []);

  if (!selectedTarget) return null;

  return (
    <PanelGroup direction="horizontal" style={{ height: '100%', display: 'flex' }}>
      {/* Left — compressed target list */}
      <Panel defaultSize={28} minSize={15} maxSize={45}>
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ px: '10px', py: '10px', borderBottom: `1px solid ${tokens.color.border.default}`, flexShrink: 0 }}>
            <span style={{ fontSize: '10px', fontWeight: 700, color: tokens.color.text.secondary, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              Objectives
            </span>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {task.targets.map((target) => (
              <CompactTargetRow
                key={target.target_id}
                target={target}
                task={task}
                isSelected={target.target_id === selectedTargetId}
                onSelect={() => onSelectTarget(target.target_id)}
              />
            ))}
          </Box>
        </Box>
      </Panel>

      {/* Drag handle */}
      <PanelResizeHandle>
        <Box
          sx={{
            width: '4px',
            height: '100%',
            backgroundColor: tokens.color.surface.subtle,
            cursor: 'col-resize',
            transition: 'background-color 0.15s ease',
            '&:hover': { backgroundColor: tokens.color.border.strong },
          }}
        />
      </PanelResizeHandle>

      {/* Right — target detail */}
      <Panel defaultSize={72} minSize={50}>
        <Box
          sx={{
            height: '100%',
            opacity: detailVisible ? 1 : 0,
            transition: 'opacity 220ms ease',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none', opacity: 1 },
          }}
        >
          <TargetDetailView key={selectedTarget.target_id} target={selectedTarget} task={task} onBack={onClose} />
        </Box>
      </Panel>
    </PanelGroup>
  );
};

export default WorkspaceSplitView;
